import { useState, useEffect, useRef, useCallback } from 'react';
import { useOnboardingVideoEvent } from './analytics/useOnboardingVideoEvent';
import { useCurrentUser } from './useCurrentUser';
import { shouldSkipOnboardingModal } from '../utils/onboardingEmailValidator';
import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = 'onboardingVideoSeen';

const ERROR_MESSAGES = {
  ASYNC_STORAGE_GET: 'AsyncStorage.getItem failed for onboarding video persistence:',
  ASYNC_STORAGE_SET: 'AsyncStorage.setItem failed for onboarding video persistence:',
  LOCAL_STORAGE_GET: 'localStorage.getItem failed for onboarding video persistence:',
  LOCAL_STORAGE_SET: 'localStorage.setItem failed for onboarding video persistence:',
  CHECK_STORAGE: 'Failed to check storage for onboarding video persistence:',
  PERSIST_SEEN: 'Failed to persist onboarding video seen flag:',
} as const;

type Platform = 'mobile' | 'web';
type StorageKey = string;

function getQueryParam(key: string): string | null {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch (error) {
    return null;
  }
}

function detectPlatform(isMobile?: boolean): Platform {
  if (isMobile !== undefined) {
    return isMobile ? 'mobile' : 'web';
  }
  const rawPlatform = getQueryParam('platform') || 'web';
  return rawPlatform === 'ios' || rawPlatform === 'android' ? 'mobile' : 'web';
}

async function getStorageItem(key: StorageKey, isMobile?: boolean): Promise<string | null> {
  if (isMobile || typeof window === 'undefined') {
    try {
      return await AsyncStorage.getItem(key);
    } catch (error) {
      console.warn(ERROR_MESSAGES.ASYNC_STORAGE_GET, error);
      return null;
    }
  } else {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn(ERROR_MESSAGES.LOCAL_STORAGE_GET, error);
      return null;
    }
  }
}

async function setStorageItem(key: StorageKey, value: string, isMobile?: boolean): Promise<void> {
  if (isMobile || typeof window === 'undefined') {
    try {
      await AsyncStorage.setItem(key, value);
    } catch (error) {
      console.warn(ERROR_MESSAGES.ASYNC_STORAGE_SET, error);
    }
  } else {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      console.warn(ERROR_MESSAGES.LOCAL_STORAGE_SET, error);
    }
  }
}

/**
 * Manages the onboarding video modal visibility and persistence.
 * - Auto-opens once per device if user hasn't seen it yet.
 * - Saves "seen" flag in storage on every open.
 * - Tracks Segment events on open (rendered), manual open (Learn More) and close.
 * - Skips modal for specific email addresses defined in the email validator utility.
 *
 * @param isMobile - When true, uses AsyncStorage for persistence. When false or undefined, uses localStorage.
 */
export function useOnboardingModalTrigger(isMobile?: boolean) {
  const { trackLearnMore, trackModalOpened, trackModalClosed } = useOnboardingVideoEvent();
  const { currentUser } = useCurrentUser();

  const [isOpen, setIsOpen] = useState(false);
  const hasAutoOpened = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  const platform = detectPlatform(isMobile);

  useEffect(() => {
    const checkAndAutoOpen = async () => {
      try {
        if (shouldSkipOnboardingModal(currentUser?.email)) {
          return;
        }

        const seen = await getStorageItem(STORAGE_KEY, isMobile);
        if (!hasAutoOpened.current && seen !== 'true') {
          setIsOpen(true);
          trackModalOpened({ platform });
          await setStorageItem(STORAGE_KEY, 'true', isMobile);
          hasAutoOpened.current = true;
        }
      } catch (error) {
        console.warn(ERROR_MESSAGES.CHECK_STORAGE, error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAndAutoOpen();
  }, [platform, trackModalOpened, isMobile, currentUser?.email]);

  useEffect(() => {
    const persistSeen = async () => {
      if (isOpen) {
        try {
          await setStorageItem(STORAGE_KEY, 'true', isMobile);
        } catch (error) {
          console.warn(ERROR_MESSAGES.PERSIST_SEEN, error);
        }
      }
    };

    persistSeen();
  }, [isOpen, isMobile]);

  const open = useCallback(() => {
    if (shouldSkipOnboardingModal(currentUser?.email)) {
      return;
    }

    trackLearnMore({ platform });
    trackModalOpened({ platform });
    setIsOpen(true);
  }, [platform, trackLearnMore, trackModalOpened, currentUser?.email]);

  const close = useCallback(() => {
    trackModalClosed({ platform });
    setIsOpen(false);
  }, [platform, trackModalClosed]);

  return { isOpen, open, close, isLoading };
}
